/**
 * Drawer 540px com detalhes do app: health (sparkline 24h placeholder),
 * cert info, configuração e ações (probe, audit timeline, edit-policy
 * disabled).
 *
 * Audit timeline (Lote 8): timeline inline cursor-paginated via
 * GET /admin/ztna/apps/{id}/audit-events; link secundário pra
 * auditoria global filtrada.
 */
import { useState } from 'react'

import { formatApiError } from '../../../api/errors'
import { lerJobBloqueando, type JobBloqueando } from '../jobBloqueado'
import {
  Alert,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { ResizableDrawer } from '@/components/ui/ResizableDrawer'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconArchive,
  IconBolt,
  IconPencil,
  IconPlayerPause,
  IconShieldCheck,
  IconPlugConnected,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { ztnaApi } from '../api'
import { foiParaGmud, notifyPendingGmud } from '../gmudNotice'
import {
  type AppHealthSummary,
  deriveHealthBucket,
  type HealthBucket,
} from '../types'
import styles from '../ZtnaAdmin.module.css'
import { AppEditModal } from './AppEditModal'
import { AppDeprovisionModal } from './AppDeprovisionModal'
import { AppAdoptionModal } from './AppAdoptionModal'
import { AppDiscardModal } from './AppDiscardModal'
import { AuditTimeline } from './AuditTimeline'

interface Props {
  app: AppHealthSummary | null
  onClose: () => void
}

function healthLabel(b: HealthBucket): string {
  return ({ healthy: 'Healthy', degraded: 'Degraded', failed: 'Failed', idle: 'Idle' } as const)[b]
}

function KvRow({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <Grid gutter="xs">
      <Grid.Col span={5}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.03em' }}>
          {k}
        </Text>
      </Grid.Col>
      <Grid.Col span={7}>
        <Text size="sm" ff={mono ? 'monospace' : undefined}>{v}</Text>
      </Grid.Col>
    </Grid>
  )
}

/** Placeholder sparkline — gera onda baseada no último latency_ms. */
function Sparkline({ baseline }: { baseline: number }) {
  // Generate 24 points around baseline; smooth pseudo-random for visual.
  const points: number[] = []
  for (let i = 0; i < 24; i++) {
    const noise = (Math.sin(i * 0.7) + Math.cos(i * 1.3)) * 0.15 + 1
    points.push(Math.max(20, baseline * noise))
  }
  const max = Math.max(...points)
  const w = 280
  const h = 40
  const pathD = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w
      const y = h - (p / max) * h
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={styles.sparkline}
      preserveAspectRatio="none"
      role="img"
      aria-label="latency sparkline 24h"
    >
      <path d={pathD} stroke="var(--ai-brand-verde, #098d93)" fill="none" strokeWidth={1.5} />
    </svg>
  )
}

export function AppDrawer({ app, onClose }: Props) {
  const probeMutation = useMutation({
    mutationFn: () => (app ? ztnaApi.probeApp(app.id) : Promise.reject('no app')),
    onSuccess: (data) => {
      notifications.show({
        color: data.ok ? 'teal' : 'red',
        title: data.ok ? 'Probe OK' : 'Probe falhou',
        message: data.ok
          ? `${data.http_status ?? '—'} · ${data.latency_ms ?? '—'}ms`
          : data.error ?? 'Falha desconhecida',
      })
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Erro no probe',
        message: 'Falha ao executar probe',
      })
    },
  })

  const queryClient = useQueryClient()
  const accessModeMutation = useMutation({
    mutationFn: (mode: 'enforced' | 'paused' | 'open') => ztnaApi.setAccessMode(app!.id, mode),
    onSuccess: (resp) => {
      // O ramo da GMUD devolve `job_id: null` e `mode` = modo ATUAL, nao o
      // solicitado. Sem esta guarda a mensagem cairia em `Modo: ${resp.mode}`
      // e anunciaria o modo antigo como se fosse o novo.
      if (foiParaGmud(resp)) {
        notifyPendingGmud(
          'a mudança de postura',
          app!.display_name || app!.slug,
          resp.gmud_id,
        )
        return
      }
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({
        queryKey: ['ztna', 'app-audit-events', app!.id],
      })
      notifications.show({
        title: 'Modo de acesso alterado',
        message: resp.job_id
          ? `Job ${resp.job_id} disparado (${resp.mechanism}). Aguarde a propagação do DNS.`
          : `Modo: ${resp.mode}.`,
        color: resp.mode === 'open' ? 'orange' : 'teal',
      })
    },
    onError: (err: Error) => {
      // O 409 NOMEIA o job que bloqueia. Ate 09/09/2026 a tela mostrava so a
      // mensagem crua do axios e o operador nao tinha o que fazer com ela — o
      // `validate` existe e so foi alcancado fora da interface (TASK-10525).
      const bloqueio = lerJobBloqueando(err)
      if (bloqueio?.acionavel) setJobBloqueando(bloqueio)
      notifications.show({
        title: 'Falha ao alterar modo',
        message: bloqueio?.acionavel
          ? `O job ${bloqueio.id} terminou aguardando validação e está segurando esta app.`
          : formatApiError(err, err.message),
        color: 'red',
      })
    },
  })

  const validarMutation = useMutation({
    mutationFn: (jobId: number) => ztnaApi.validateJob(jobId),
    onSuccess: (_r, jobId) => {
      setJobBloqueando(null)
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'jobs'] })
      notifications.show({
        title: 'Job validado',
        message: `Job ${jobId} marcado como validado. Tente a ação de novo.`,
        color: 'teal',
      })
    },
    onError: (err: Error) => notifications.show({
      title: 'Falha ao validar',
      message: formatApiError(err, err.message),
      color: 'red',
    }),
  })

  const [jobBloqueando, setJobBloqueando] =
    useState<JobBloqueando | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [deprovisionOpen, setDeprovisionOpen] = useState(false)
  const [adocaoOpen, setAdocaoOpen] = useState(false)
  const [descarteOpen, setDescarteOpen] = useState(false)

  if (!app) return null
  const bucket = deriveHealthBucket(app)
  const certWarn =
    app.cert_expires_at && dayjs(app.cert_expires_at).diff(dayjs(), 'day') < 30

  return (
    <ResizableDrawer
      storageKey="ztna-app-drawer-width"
      defaultWidth={540}
      minWidth={420}
      opened={!!app}
      onClose={onClose}
      position="right"
      withCloseButton
      title={
        <Group gap="xs">
          <span
            className={`${styles.statusDot} ${styles[bucket]}`}
            aria-label={`status: ${healthLabel(bucket)}`}
            role="img"
          />
          <Title order={4} title={app.slug}>{app.display_name || app.slug}</Title>
          <Badge variant="light" size="sm" color="gray">
            {app.environment}
          </Badge>
        </Group>
      }
    >
      <Stack gap="md">
        {app.access_mode === 'enforced' && (
          <Alert
            icon={<IconShieldCheck size={18} />}
            color="green"
            variant="light"
            title="ZTNA Ativo"
          >
            Tráfego de <strong>{app.hostname}</strong> está passando pelo broker
            (autenticação + policy gate). Para pausar ou abrir o acesso, use o
            controle "Estado" abaixo.
          </Alert>
        )}
        {app.access_mode === 'paused' && (
          <Alert
            icon={<IconPlayerPause size={18} />}
            color="orange"
            variant="light"
            title="ZTNA Pausado"
          >
            Broker retorna <strong>404</strong> em <strong>{app.hostname}</strong>.
            DNS continua apontando pro broker; usuários verão tela de erro até
            retomada. Para retomar, selecione "Ativo" abaixo. Para remover do
            ZTNA permanentemente, use{' '}
            {app.tem_historico ? '"Excluir / sair do ZTNA"' : '"Descartar do catálogo"'}.
          </Alert>
        )}
        {app.access_mode === 'open' && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            variant="filled"
            title="Acesso ABERTO — autenticação DESLIGADA"
          >
            Qualquer pessoa com a URL de <strong>{app.hostname}</strong> acessa o
            site <strong>sem login</strong>. Clique em "Voltar pro ZTNA" abaixo
            para religar a autenticação.
          </Alert>
        )}
        <section>
          <Title order={6} mb="xs" c="var(--ai-brand-azul)">
            Health (últimas 24h)
          </Title>
          <Sparkline baseline={app.last_latency_ms ?? 100} />
          <Group gap="xl" mt="xs">
            <div>
              <Text size="xs" c="dimmed">Status</Text>
              <Text size="sm" fw={600}>● {healthLabel(bucket)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Última latência</Text>
              <Text size="sm" ff="monospace">
                {app.last_latency_ms ? `${app.last_latency_ms}ms` : '—'}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">HTTP</Text>
              <Text size="sm" ff="monospace">
                {app.last_http_status ?? '—'}
              </Text>
            </div>
          </Group>
        </section>

        <Divider />

        <section>
          <Title order={6} mb="xs" c="var(--ai-brand-azul)">
            Certificado ACM
          </Title>
          <Stack gap={6}>
            <KvRow
              k="Expira em"
              mono
              v={
                <span style={{ color: certWarn ? 'var(--ai-warning)' : undefined }}>
                  {app.cert_expires_at
                    ? `${dayjs(app.cert_expires_at).format('YYYY-MM-DD HH:mm')}`
                    : '—'}
                </span>
              }
            />
            <KvRow k="Hostname" mono v={app.hostname} />
            <KvRow
              k="Status"
              v={
                certWarn ? (
                  <Badge color="yellow" variant="light">
                    Renovar
                  </Badge>
                ) : (
                  <Badge color="green" variant="light">OK</Badge>
                )
              }
            />
          </Stack>
        </section>

        <Divider />

        <section>
          <Title order={6} mb="xs" c="var(--ai-brand-azul)">
            Estado
          </Title>
          {jobBloqueando?.acionavel && (
            <Alert color="yellow" title="Um job terminado está segurando esta app">
              <Stack gap="xs">
                <Text size="sm">
                  O job {jobBloqueando.id} terminou aguardando validação. Ele não
                  vai continuar sozinho, e enquanto estiver assim as ações desta
                  app são recusadas.
                </Text>
                <Group>
                  <Button
                    size="xs"
                    color="yellow"
                    loading={validarMutation.isPending}
                    onClick={() => validarMutation.mutate(jobBloqueando.id)}
                  >
                    Validar job {jobBloqueando.id}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setJobBloqueando(null)}
                  >
                    Dispensar
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
          <SegmentedControl
            fullWidth
            value={app.access_mode}
            disabled={accessModeMutation.isPending}
            data={[
              { label: 'Ativo', value: 'enforced' },
              { label: 'Pausado', value: 'paused' },
              { label: 'Aberto', value: 'open' },
            ]}
            onChange={(next) => {
              const labels: Record<string, string> = {
                enforced: 'Ativo (ZTNA)',
                paused: 'Pausado (404)',
                open: 'Aberto (sem auth)',
              }
              modals.openConfirmModal({
                title: `Mudar acesso de ${app.hostname}?`,
                children: (
                  <Stack gap="xs">
                    <Text size="sm">Novo modo: <strong>{labels[next]}</strong>.</Text>
                    {next === 'open' && (
                      <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                        A <strong>autenticação será DESLIGADA</strong>. Durante a janela, qualquer
                        pessoa na internet com a URL acessa o site sem login.
                        {app.identity_mode === 'oidc' && ' (App OIDC: o upstream pode ainda exigir login próprio.)'}
                      </Alert>
                    )}
                    {next === 'paused' && <Text size="xs" c="dimmed">Broker retorna 404 até retomar.</Text>}
                  </Stack>
                ),
                labels: { confirm: 'Confirmar', cancel: 'Cancelar' },
                confirmProps: { color: next === 'open' ? 'red' : next === 'paused' ? 'yellow' : 'teal' },
                onConfirm: () => accessModeMutation.mutate(next as 'enforced' | 'paused' | 'open'),
              })
            }}
          />
          {app.access_mode === 'open' && (
            <Button
              mt="sm"
              variant="light"
              color="teal"
              leftSection={<IconShieldCheck size={16} />}
              loading={accessModeMutation.isPending}
              onClick={() => accessModeMutation.mutate('enforced')}
            >
              Voltar pro ZTNA
            </Button>
          )}
        </section>

        <Divider />

        <section>
          <Title order={6} mb="xs" c="var(--ai-brand-azul)">
            Configuração
          </Title>
          <Stack gap={6}>
            <KvRow k="Tenant" v={`#${app.tenant_id}`} />
            <KvRow k="Slug" mono v={app.slug} />
            <KvRow k="Environment" v={<Badge variant="light" size="sm">{app.environment}</Badge>} />
            <KvRow k="Identity mode" v={app.identity_mode.toUpperCase()} />
            <KvRow k="Routing mode" v={app.routing_mode.toUpperCase()} />
            <KvRow k="Enabled" v={app.enabled ? 'Sim' : 'Não'} />
          </Stack>
        </section>

        <Divider />

        <section>
          <Title order={6} mb="xs" c="var(--ai-brand-azul)">
            Audit timeline
          </Title>
          <AuditTimeline
            appId={app.id}
            globalAuditHref={`/auditoria-global?resource_type=application&resource_id=${app.id}`}
          />
        </section>

        <Divider />

        <Group gap="xs" mt="md">
          <Button
            leftSection={<IconBolt size={14} />}
            loading={probeMutation.isPending}
            onClick={() => probeMutation.mutate()}
          >
            Probe agora
          </Button>
          <Button
            variant="default"
            leftSection={<IconPencil size={14} />}
            onClick={() => setEditOpen(true)}
          >
            Editar
          </Button>
          {/* App criada FORA do pipeline: os três verbos de ciclo de vida
              devolvem 422 nela, e o caminho é adotar (ou descartar). Descartada
              não volta por aqui — ver MIGRATION_247. */}
          {!app.tem_historico && app.discarded_at === null && (
            <Button
              variant="light"
              color="inovareTeal"
              leftSection={<IconPlugConnected size={14} />}
              onClick={() => setAdocaoOpen(true)}
            >
              Adotar…
            </Button>
          )}
          {/* O destrutivo decide por `tem_historico`, e o rótulo diz qual é.
              Um botão com dois significados foi o que produziu o incidente de
              31/08/2026: "Excluir / sair do ZTNA" aparecia sem condição, e na
              app sem histórico ele só podia devolver 422 `no_provision_history`
              — a mesma armadilha do "desabilitado convidaria a insistir" que a
              modal de adoção já nomeia. Com histórico existe infraestrutura
              NOSSA de pé e o verbo reverte; sem histórico não há o que reverter
              e o verbo é o descarte, que não toca em AWS. Já descartada não
              oferece nenhum dos dois: ela já está fora do catálogo. */}
          {app.tem_historico ? (
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => setDeprovisionOpen(true)}
            >
              Excluir / sair do ZTNA
            </Button>
          ) : app.discarded_at === null ? (
            <Button
              variant="light"
              color="orange"
              leftSection={<IconArchive size={14} />}
              onClick={() => setDescarteOpen(true)}
            >
              Descartar do catálogo
            </Button>
          ) : null}
        </Group>
      </Stack>
      {adocaoOpen ? (
        <AppAdoptionModal
          app={app}
          opened={adocaoOpen}
          onClose={() => setAdocaoOpen(false)}
        />
      ) : null}
      {editOpen ? (
        <AppEditModal app={app} opened={editOpen} onClose={() => setEditOpen(false)} />
      ) : null}
      {deprovisionOpen ? (
        <AppDeprovisionModal
          app={app}
          opened={deprovisionOpen}
          onClose={() => setDeprovisionOpen(false)}
        />
      ) : null}
      {descarteOpen ? (
        <AppDiscardModal
          app={app}
          opened={descarteOpen}
          onClose={() => setDescarteOpen(false)}
        />
      ) : null}
    </ResizableDrawer>
  )
}

export default AppDrawer
